import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Copy, CheckCircle, Users, Mail, Calendar, Star,
  TrendingUp, BookOpen, AlertCircle, Send
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface FreeProfileCoach {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  created_at: string;
  free_profile_onboarding_completed: boolean;
  free_profile_completed_assessments: number;
  free_profile_founder_session_booked: boolean;
  free_profile_upgrade_status: string;
  tier: string;
  free_profile_coaching_niche: string | null;
  invitationCount: number;
  aiAnalysisCount: number;
}

export function FreeProfileReferralTools() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coaches, setCoaches] = useState<FreeProfileCoach[]>([]);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [partnerCode, setPartnerCode] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    loadData();
  }, [user?.id]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();
      setUserRole(userData?.role || null);

      let filterPartnerId: string | null = null;

      if (userData?.role === 'partner') {
        const { data: partner } = await supabase
          .from('partners')
          .select('id, free_profile_code')
          .eq('user_id', user.id)
          .maybeSingle();
        filterPartnerId = partner?.id || null;
        setPartnerId(filterPartnerId);
        setPartnerCode(partner?.free_profile_code || null);
      }

      let query = supabase
        .from('coaches')
        .select(`
          id, user_id, created_at, tier,
          free_profile_onboarding_completed,
          free_profile_completed_assessments,
          free_profile_founder_session_booked,
          free_profile_upgrade_status,
          free_profile_coaching_niche,
          users!coaches_user_id_fkey(full_name, email)
        `)
        .eq("account_plan", 'FREE_PROFILE_COACH')
        .order('created_at', { ascending: false });

      if (filterPartnerId) {
        query = query.eq('partner_id', filterPartnerId);
      }

      const { data: coachesData } = await query;

      if (coachesData) {
        const mapped: FreeProfileCoach[] = await Promise.all(
          (coachesData as any[]).map(async (c) => {
            const { count: invCount } = await supabase
              .from('respondents')
              .select('*', { count: 'exact', head: true })
              .eq('coach_id', c.id);

            const { count: aiCount } = await supabase
              .from('free_profile_funnel_events')
              .select('*', { count: 'exact', head: true })
              .eq('coach_id', c.id)
              .eq('event_type', 'first_ai_analysis_generated');

            return {
              id: c.id,
              user_id: c.user_id,
              full_name: c.users?.full_name || 'Unknown',
              email: c.users?.email || '',
              created_at: c.created_at,
              free_profile_onboarding_completed: c.free_profile_onboarding_completed,
              free_profile_completed_assessments: c.free_profile_completed_assessments || 0,
              free_profile_founder_session_booked: c.free_profile_founder_session_booked || false,
              free_profile_upgrade_status: c.free_profile_upgrade_status || 'none',
              tier: c.tier,
              free_profile_coaching_niche: c.free_profile_coaching_niche,
              invitationCount: invCount || 0,
              aiAnalysisCount: aiCount || 0,
            };
          })
        );
        setCoaches(mapped);
      }
    } catch (err) {
      console.error('Error loading referral tools:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const referralLink = partnerCode
    ? `https://my.nlpos.app/free-profile?ref=${partnerCode}`
    : partnerId
    ? `https://my.nlpos.app/free-profile?ref=${partnerId}`
    : 'https://my.nlpos.app/free-profile';

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resendActivationEmail = async (coach: FreeProfileCoach) => {
    setResendingId(coach.id);
    try {
      await supabase.functions.invoke('send-email', {
        body: {
          type: 'free_profile_no_activation',
          recipientEmail: coach.email,
          recipientName: coach.full_name,
          loginUrl: 'https://my.nlpos.app/login',
        },
      });
    } catch {
      // best-effort
    } finally {
      setResendingId(null);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading referral tools..." />;
  }

  if (userRole !== 'partner' && userRole !== 'admin') {
    return (
      <div className="min-h-screen freud-bg py-8 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="freud-card-elevated p-8 text-center">
            <AlertCircle className="w-8 h-8 text-warm-600 mx-auto mb-4" />
            <p className="freud-text-base text-chocolate-600">You do not have access to this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen freud-bg py-8 px-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="freud-heading-2xl text-chocolate-900">Free Profile Referral Tools</h1>
          <p className="freud-text-base text-chocolate-500">
            Share your unique link and track coaches who sign up through your referral.
          </p>
        </div>

        {/* Referral link card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="freud-card-elevated p-6"
        >
          <h3 className="freud-heading-base text-chocolate-900 mb-2">Your Unique Referral Link</h3>
          <p className="freud-text-sm text-chocolate-500 mb-4">
            Share this link with coaches you want to invite to the free Human Potential Profile account.
          </p>
          {partnerCode && (
            <div className="mb-4 p-3 bg-sage-50 border border-sage-200 rounded-xl flex items-center gap-3">
              <span className="freud-text-sm text-chocolate-600">Activation code:</span>
              <span className="font-mono font-bold text-sage-700 tracking-widest text-base">{partnerCode}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(partnerCode); }}
                className="ml-auto flex items-center gap-1 freud-text-xs text-sage-600 hover:text-sage-700 font-medium"
              >
                <Copy className="w-3 h-3" />
                Copy code
              </button>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-cream-100 rounded-xl p-4">
              <p className="freud-text-sm text-chocolate-700 font-mono break-all">{referralLink}</p>
            </div>
            <button
              onClick={copyReferralLink}
              className="freud-btn-primary flex items-center gap-2 flex-shrink-0"
            >
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy Link'}
            </button>
          </div>
        </motion.div>

        {/* Coach status table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="freud-card-elevated p-6"
        >
          <h3 className="freud-heading-base text-chocolate-900 mb-4">
            Coaches Who Registered Through Your Link
          </h3>

          {coaches.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-8 h-8 text-chocolate-300 mx-auto mb-3" />
              <p className="freud-text-sm text-chocolate-400">
                No coaches have signed up through your link yet. Share your referral link to get started.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-chocolate-100">
                    <th className="py-3 px-2 freud-text-xs font-bold text-chocolate-400 uppercase tracking-wider">Coach</th>
                    <th className="py-3 px-2 freud-text-xs font-bold text-chocolate-400 uppercase tracking-wider">Email</th>
                    <th className="py-3 px-2 freud-text-xs font-bold text-chocolate-400 uppercase tracking-wider">Signup</th>
                    <th className="py-3 px-2 freud-text-xs font-bold text-chocolate-400 uppercase tracking-wider">Onboarded</th>
                    <th className="py-3 px-2 freud-text-xs font-bold text-chocolate-400 uppercase tracking-wider">Invites</th>
                    <th className="py-3 px-2 freud-text-xs font-bold text-chocolate-400 uppercase tracking-wider">Profiles</th>
                    <th className="py-3 px-2 freud-text-xs font-bold text-chocolate-400 uppercase tracking-wider">AI Analyses</th>
                    <th className="py-3 px-2 freud-text-xs font-bold text-chocolate-400 uppercase tracking-wider">Session</th>
                    <th className="py-3 px-2 freud-text-xs font-bold text-chocolate-400 uppercase tracking-wider">Upgrade</th>
                    <th className="py-3 px-2 freud-text-xs font-bold text-chocolate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coaches.map((coach) => (
                    <tr key={coach.id} className="border-b border-chocolate-50 hover:bg-cream-50">
                      <td className="py-3 px-2">
                        <p className="freud-text-sm font-medium text-chocolate-900">{coach.full_name}</p>
                        {coach.free_profile_coaching_niche && (
                          <p className="freud-text-xs text-chocolate-400">{coach.free_profile_coaching_niche}</p>
                        )}
                      </td>
                      <td className="py-3 px-2 freud-text-sm text-chocolate-600">{coach.email}</td>
                      <td className="py-3 px-2 freud-text-xs text-chocolate-500">
                        {new Date(coach.created_at).toLocaleDateString('en-GB')}
                      </td>
                      <td className="py-3 px-2">
                        {coach.free_profile_onboarding_completed ? (
                          <CheckCircle className="w-4 h-4 text-sage-600" />
                        ) : (
                          <span className="freud-text-xs text-chocolate-400">Pending</span>
                        )}
                      </td>
                      <td className="py-3 px-2 freud-text-sm text-chocolate-700">{coach.invitationCount}</td>
                      <td className="py-3 px-2 freud-text-sm text-chocolate-700">{coach.free_profile_completed_assessments}</td>
                      <td className="py-3 px-2 freud-text-sm text-chocolate-700">{coach.aiAnalysisCount}</td>
                      <td className="py-3 px-2">
                        {coach.free_profile_founder_session_booked ? (
                          <CheckCircle className="w-4 h-4 text-sage-600" />
                        ) : (
                          <span className="freud-text-xs text-chocolate-400">Not booked</span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          coach.free_profile_upgrade_status === 'completed'
                            ? 'bg-sage-100 text-sage-700'
                            : coach.free_profile_upgrade_status === 'pending'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-cream-100 text-chocolate-400'
                        }`}>
                          {coach.free_profile_upgrade_status}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <button
                          onClick={() => resendActivationEmail(coach)}
                          disabled={resendingId === coach.id}
                          className="flex items-center gap-1 text-sage-600 hover:text-sage-700 freud-text-xs font-medium transition-colors"
                        >
                          <Send className="w-3 h-3" />
                          Resend
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
